import { Component, EventEmitter, Input, OnInit, Output, ViewChild, ChangeDetectionStrategy, OnDestroy } from '@angular/core';
import { MediaObserver } from '@angular/flex-layout';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { stripeCardValidatorList } from 'src/app/validators';
import { InputComponent } from 'src/app/shared/input/input.component';
import { slideY } from 'src/app/animations';
import { BehaviorSubject } from 'rxjs';
import Subscriber from 'src/app/subscriber';
import { today } from 'src/app/functions';
import { isEqual } from 'lodash';
import { IFormPaymentData } from './form-payment.interfaces';
import { EFormPaymentType } from './form-payment.enums';

@Component({
  selector: 'app-form-payment',
  templateUrl: './form-payment.component.html',
  styleUrls: ['./form-payment.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [slideY()],
})
export class FormPaymentComponent implements OnInit, OnDestroy {
  @ViewChild('inputCard') inputCardComponent: InputComponent;

  @Input() isGift?: boolean;

  /** `undefined` when invalid */
  @Input() set data(data: IFormPaymentData | undefined) {
    if (!isEqual(data, this.data)) {
      if (data) {
        this.typeFormControl.setValue(data.type);
        this.formGroup.setValue(data);
      }

      this.dataChange.emit(data);
    }
  }

  get data() {
    return this.formGroup.value;
  }

  @Output() dataChange = new EventEmitter<IFormPaymentData | undefined>();

  @Input() set touchEventEmitter(e: EventEmitter<void>) {
    e.subscribe(() => {
      this.formGroup.markAllAsTouched();
    })
  }

  @Input() submitButtonDisabled: boolean = false;

  @Output() submitButtonClick = new EventEmitter<void>();

  public typeFormControl = new FormControl(null, [Validators.required]);

  public cardFormGroup = new FormGroup({
    type: this.typeFormControl,
    card: new FormControl(null, stripeCardValidatorList),
    name: new FormControl(''),
    pmToken: new FormControl(null),
    terms: new FormControl(false, [Validators.requiredTrue]),
  })

  public konbiniFormGroup = new FormGroup({
    type: this.typeFormControl,
    terms: new FormControl(false, [Validators.requiredTrue]),
  })

  public readonly EFormPaymentType = EFormPaymentType;

  public readonly shippingDate = new BehaviorSubject('');

  public readonly nextShippingDate = new BehaviorSubject('');

  public readonly endOfCycleDate = new BehaviorSubject('');

  public readonly isLtSm = new BehaviorSubject(false);

  public readonly isLtMd = new BehaviorSubject(false);

  private readonly _sub = new Subscriber();

  get formGroup(): FormGroup {
    return this.typeFormControl.value === EFormPaymentType.konbini
      ? this.konbiniFormGroup
      : this.cardFormGroup;
  }

  constructor(
    private _media: MediaObserver,
  ) { }

  ngOnInit(): void {
    this._sub.push(
      this.cardFormGroup.valueChanges.subscribe((data: IFormPaymentData) => {
        this._onDataChange(data)
      }),

      this.konbiniFormGroup.valueChanges.subscribe((data: IFormPaymentData) => {
        this._onDataChange(data)
      }),

      this._media.asObservable().subscribe(() => {
        this._updateIsLtSm();
        this._updateIsLtMd();
      }),
    );

    this._updateDates();

    // async each minute update
    const intervalId = window.setInterval(() => {
      this._updateDates();
    }, 60000);

    this._sub.on.once(Subscriber.events.unsubscribe, () => {
      window.clearInterval(intervalId);
    })
  }

  ngOnDestroy(): void {
    this._sub.unsubscribe();
  }

  private _onDataChange(data: IFormPaymentData) {
    if (this.formGroup.valid) {
      this.dataChange.emit(data);
    } else {
      this.dataChange.emit(undefined);
    }
  }

  private _updateIsLtSm(): void {
    this.isLtSm.next(this._media.isActive('lt-sm'));
  }

  private _updateIsLtMd(): void {
    this.isLtMd.next(this._media.isActive('lt-md'));
  }

  private _updateDates(): void {
    this._updateShippingDate();
    this._updateNextShippingDate();
    this._updateEndOfCycleDate();
  }

  private _updateShippingDate(): void {
    this.shippingDate.next(today.getExpectedShippingDate('YYYY/MM/DD'));
  }

  private _updateNextShippingDate(): void {
    this.nextShippingDate.next(today.getNextExpectedShippingDate('YYYY/MM/DD'))
  }

  private _updateEndOfCycleDate(): void {
    this.endOfCycleDate.next(today.getEndOfCycleDate('YYYY/MM/DD'));
  }
}
